<script setup>
import { ref } from 'vue';
import CopilotEditor from 'dashboard/components/widgets/WootWriter/CopilotEditor.vue';
import CaptainLoader from 'dashboard/components/widgets/conversation/copilot/CaptainLoader.vue';

defineProps({
  showCopilotEditor: {
    type: Boolean,
    default: false,
  },
  isGeneratingContent: {
    type: Boolean,
    default: false,
  },
  generatedContent: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'focus',
  'blur',
  'clearSelection',
  'contentReady',
  'send',
  'close',
]);

const copilotEditorContent = ref('');

const onFocus = () => {
  emit('focus');
};

const onBlur = () => {
  emit('blur');
};

const clearEditorSelection = () => {
  emit('clearSelection');
};

const onSend = () => {
  emit('send', copilotEditorContent.value);
  copilotEditorContent.value = '';
};
</script>

<template>
  <Transition
    mode="out-in"
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    @after-enter="emit('contentReady')"
  >
    <CopilotEditor
      v-if="showCopilotEditor && !isGeneratingContent"
      key="copilot-editor"
      v-model="copilotEditorContent"
      class="copilot-editor"
      :generated-content="generatedContent"
      :placeholder="placeholder"
      :min-height="4"
      :enabled-menu-options="[]"
      @focus="onFocus"
      @blur="onBlur"
      @clear-selection="clearEditorSelection"
      @send="onSend"
      @close="emit('close')"
    />
    <div
      v-else-if="isGeneratingContent"
      key="loading-state"
      class="copilot-thinking"
    >
      <div class="copilot-thinking__icon">
        <CaptainLoader class="copilot-thinking__logo size-5" />
      </div>
      <div class="copilot-thinking__body">
        <span class="copilot-thinking__text">
          {{ $t('CONVERSATION.REPLYBOX.COPILOT_THINKING') }}
        </span>
        <span class="copilot-thinking__dots" aria-hidden="true">
          <i /><i /><i />
        </span>
      </div>
      <span class="copilot-thinking__sweep" aria-hidden="true" />
    </div>
  </Transition>
</template>

<style lang="scss">
.copilot-editor {
  .ProseMirror-menubar {
    display: none;
  }
}

.copilot-thinking {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 4.75rem;
  margin-bottom: 1rem;
  padding: 0.875rem 1rem;
  overflow: hidden;
  border-radius: 0.75rem;
  border: 1px solid rgba(99, 102, 241, 0.28);
  background: linear-gradient(
    120deg,
    rgba(59, 130, 246, 0.12),
    rgba(139, 92, 246, 0.12) 55%,
    rgba(59, 130, 246, 0.12)
  );
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}

.copilot-thinking__icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  border-radius: 9999px;
  background: rgba(99, 102, 241, 0.16);
}

.copilot-thinking__icon::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: inherit;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.5), transparent 70%);
  filter: blur(7px);
  animation: copilot-glow 1.8s ease-in-out infinite;
}

.copilot-thinking__logo {
  position: relative;
  color: #6366f1;
}

.copilot-thinking__body {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.copilot-thinking__text {
  font-size: 0.875rem;
  font-weight: 600;
  background: linear-gradient(
    90deg,
    #60a5fa 0%,
    #a78bfa 25%,
    #60a5fa 50%,
    #a78bfa 75%,
    #60a5fa 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: copilot-shimmer 2.4s linear infinite;
}

.copilot-thinking__dots {
  display: inline-flex;
  align-items: flex-end;
  gap: 0.18rem;
  padding-bottom: 2px;
}

.copilot-thinking__dots i {
  width: 4px;
  height: 4px;
  display: inline-block;
  border-radius: 9999px;
  background: #8b5cf6;
  animation: copilot-bounce 1.2s ease-in-out infinite;
}

.copilot-thinking__dots i:nth-child(2) {
  animation-delay: 0.18s;
}

.copilot-thinking__dots i:nth-child(3) {
  animation-delay: 0.36s;
}

.copilot-thinking__sweep {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    100deg,
    transparent 30%,
    rgba(255, 255, 255, 0.35) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  animation: copilot-sweep 2.2s ease-in-out infinite;
}

@keyframes copilot-shimmer {
  to {
    background-position: -200% 0;
  }
}

@keyframes copilot-bounce {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  50% {
    transform: translateY(-3px);
    opacity: 1;
  }
}

@keyframes copilot-glow {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
}

@keyframes copilot-sweep {
  0% {
    transform: translateX(-100%);
  }
  60%,
  100% {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .copilot-thinking__text,
  .copilot-thinking__dots i,
  .copilot-thinking__icon::before,
  .copilot-thinking__sweep {
    animation: none;
  }
}
</style>
